import { get } from 'svelte/store';

import { aiModeActive } from '$lib/stores/ai';
import { guidedActive, openDoor, returnToDoor, type Door } from '$lib/stores/guided';
import { applicationMode } from '$lib/stores/mode';
import { showDataSelectionModal } from '$lib/stores/modal';
import { exportHistoryOpen } from '$lib/stores/ui';
import { closeSavedList, openSavedList } from '$lib/stores/savedlists';

/**
 * Going somewhere.
 *
 * # Why transitions are functions
 *
 * Because no single store says where you are — `screenFor` reads nine of them —
 * so *arriving* somewhere means writing several in the right combination, and
 * every caller that did it by hand had to know which. Opening a saved list from
 * the sidebar had to remember to leave the guide; leaving the guide for a
 * selection had to remember to close the list; loading a list as a selection
 * had to set the mode as well, because the data view only exists in classic.
 *
 * Missing one is silent. You do not get an error, you get the previous screen's
 * furniture around the new screen's content — which is exactly the pair of bugs
 * this app has already had.
 *
 * So each destination is one function, and it says everything that must be true
 * on arrival rather than only the part the caller was thinking about. Adding a
 * screen means adding a function here, not auditing every existing one.
 *
 * # These write stores; they do not route
 *
 * Two destinations are real routes — the company profile and the sign-in screen
 * — and they stay with `goto` at their call sites. Everything else is a screen
 * this app draws without changing the URL, which is a thing worth fixing and is
 * not what this file is for. See docs/refactor/screens.md.
 */

/**
 * Leave every screen that overlays the app.
 *
 * The shared preamble of *every* destination below, including the ones whose
 * own screen is an overlay: they clear the lot and then assert their own,
 * rather than naming the two or three they happen to know about.
 *
 * That rule was learned rather than designed. `showAssistant` and `showPicker`
 * used to list what they cleared by hand, and when the export history was added
 * as a fourth overlay both silently kept it on screen underneath — the failure
 * this module exists to prevent, reproduced inside the module itself. Clearing
 * everything costs a redundant store write and cannot be forgotten.
 *
 * Not exported: "close everything" is not a place, and a caller that wanted it
 * would be a caller that had not decided where it was going.
 */
function leaveOverlays(): void {
  closeSavedList();
  exportHistoryOpen.set(false);
  aiModeActive.set(false);
  showDataSelectionModal.set(false);
}

/**
 * The front screen — the members' home, or the guide's first question.
 *
 * `resetGuided` is deliberately not called: this is navigation, and throwing
 * away a count somebody is part-way through collecting is not what clicking
 * "Dashboard" asks for.
 */
export function showFrontDoor(): void {
  leaveOverlays();
  applicationMode.set('classic');
  returnToDoor();
}

/** Take one of the four doors, starting the guide at that door's first step. */
export function showDoor(door: Door): void {
  leaveOverlays();
  applicationMode.set('classic');
  // Which step follows a door is the guide's own business, and stays there.
  // This adds only what *arriving* requires.
  openDoor(door);
}

/**
 * The data view, over whatever selection is currently seated.
 *
 * Does not seat one — see `seatSelection`, which is about the rows. This is
 * about which screen is looking at them, and the two are separate because a
 * search seats rows from a screen that is already the data view.
 */
export function showResults(): void {
  leaveOverlays();
  applicationMode.set('classic');
  guidedActive.set(false);
}

/** One saved list. */
export async function showList(id: string): Promise<void> {
  leaveOverlays();
  await openSavedList(id);
}

/** What this organisation has exported. */
export function showExportHistory(): void {
  leaveOverlays();
  exportHistoryOpen.set(true);
}

/** The assistant. */
export function showAssistant(): void {
  leaveOverlays();
  applicationMode.set('classic');
  aiModeActive.set(true);
}

/** The industry picker. */
export function showPicker(): void {
  leaveOverlays();
  showDataSelectionModal.set(true);
}

/**
 * Whether the app is already showing the data view.
 *
 * For callers that seat rows and want to know whether they also need to
 * navigate — a search run from the assistant does, one run from the filter bar
 * does not.
 */
export function alreadyOnResults(): boolean {
  return get(applicationMode) === 'classic' && !get(aiModeActive) && !get(guidedActive);
}
